import { isUnsupportedPreassignmentBatch } from '../api/preassignments';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Label } from '@rudi/ui/label';
import { Textarea } from '@rudi/ui/textarea';
import { toast } from 'sonner';
import {
  useCancelTeamPreassignment,
  useCreateTeamPreassignments,
  useCreateLegacyTeamPreassignment,
  useTeamPreassignments,
  useTenantRoles,
} from '../api/hooks';
import type { AdminTeam } from '../api/types';

function errorDetail(error: unknown): string {
  const data = (error as { response?: { data?: { detail?: unknown; error?: unknown } } }).response
    ?.data;
  const detail = data?.detail ?? data?.error;
  return typeof detail === 'string'
    ? detail
    : 'Could not save preassignments. Check the email addresses and try again.';
}

export function TeamPreassignmentsPanel({ tenantId, team }: { tenantId: string; team: AdminTeam }) {
  const query = useTeamPreassignments(tenantId, team.id);
  const { data: roles } = useTenantRoles(tenantId);
  const create = useCreateTeamPreassignments(tenantId, team.id);
  const legacyCreate = useCreateLegacyTeamPreassignment(tenantId, team.id);
  const [legacyMode, setLegacyMode] = useState(false);
  const cancel = useCancelTeamPreassignment(tenantId, team.id);
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const emails = [
    ...new Set(
      input
        .split(/[\s,;]+/)
        .map(value => value.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
  const claimedIds = (query.data ?? [])
    .filter(row => row.status === 'claimed')
    .map(row => row.id)
    .join(',');
  const previousClaims = useRef(claimedIds);

  useEffect(() => {
    if (previousClaims.current !== claimedIds) {
      previousClaims.current = claimedIds;
      qc.invalidateQueries({ queryKey: ['team-members', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-members', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] });
    }
  }, [claimedIds, qc, tenantId]);

  // The existing roster remains usable against a backend released before setup.
  if ((query.error as { response?: { status?: number } } | null)?.response?.status === 404)
    return null;

  const save = async () => {
    try {
      if (legacyMode) {
        const email = emails[0];
        const roleName = roleNames[email] ?? 'clinical_trials_manager';
        if (
          emails.length !== 1 ||
          (roleName !== 'collaborator' && roleName !== 'clinical_trials_manager')
        )
          return;
        await legacyCreate.mutateAsync({ email, roleName });
      } else {
        await create.mutateAsync(
          emails.map(email => ({ email, roleName: roleNames[email] ?? 'clinical_trials_manager' }))
        );
      }
      setInput('');
      setRoleNames({});
      toast.success('Preassignments saved. No emails were sent.');
    } catch (error) {
      if (!legacyMode && isUnsupportedPreassignmentBatch(error)) {
        setLegacyMode(true);
        setRoleNames({});
        toast.error('Add one email at a time on this server. No assignments were saved.');
      } else {
        toast.error(errorDetail(error));
      }
    }
  };

  const cancelOne = async (id: string) => {
    try {
      await cancel.mutateAsync(id);
      toast.success('Preassignment cancelled');
    } catch (error) {
      toast.error(errorDetail(error));
    }
  };

  return (
    <section className="space-y-3 rounded-3xl border p-4" aria-label="Advance team setup">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">People awaiting sign-up</h3>
        <Button size="sm" variant="ghost" onClick={() => query.refetch()}>
          Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Preassign people before they create an account. Registered teammates can already view their
        Clinical Trials records. They join after verifying their email and signing in. Preparation
        sends no emails. Cancel pending assignments before archiving.
      </p>
      {query.isLoading && <p className="text-sm">Loading preassignments…</p>}
      {query.error && (
        <p role="alert" className="text-sm text-destructive">
          Could not load preassignments. Try refreshing.
        </p>
      )}
      {(query.data ?? [])
        .filter(row => row.status === 'pending')
        .map(row => (
          <div key={row.id} className="flex items-start justify-between gap-3 text-sm">
            <div>
              <span>{row.email}</span>
              <Badge variant={row.failureReason ? 'destructive' : 'secondary'} className="ml-2">
                {row.failureReason ? 'Needs attention' : 'Pending sign-up'}
              </Badge>
              <p className="text-xs text-muted-foreground">{row.roleDisplayName}</p>
              {row.failureReason && (
                <p role="alert" className="text-xs text-destructive">
                  {row.failureReason}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={cancel.isPending}
              onClick={() => cancelOne(row.id)}
            >
              Cancel preassignment
            </Button>
          </div>
        ))}
      {(query.data ?? [])
        .filter(row => row.status === 'claimed')
        .map(row => (
          <p key={row.id} className="text-xs text-muted-foreground">
            {row.email} · Joined · {row.roleDisplayName} at sign-up
          </p>
        ))}
      {team.status === 'active' && !query.isLoading && !query.error && (
        <div className="space-y-2 border-t pt-3">
          {legacyMode && (
            <p className="text-sm">
              Add one email at a time. Available roles: Collaborator and Clinical Trials Manager.
            </p>
          )}
          <Label htmlFor={`preassign-emails-${team.id}`}>Email addresses</Label>
          <Textarea
            id={`preassign-emails-${team.id}`}
            value={input}
            onChange={event => setInput(event.target.value)}
            placeholder="One email per line"
            rows={3}
          />
          {emails.map(email => (
            <div key={email} className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor={`preassign-role-${team.id}-${email}`}>{email} role</Label>
              <select
                id={`preassign-role-${team.id}-${email}`}
                className="h-9 rounded-3xl border bg-background px-3 text-sm"
                value={roleNames[email] ?? 'clinical_trials_manager'}
                onChange={event =>
                  setRoleNames(current => ({ ...current, [email]: event.target.value }))
                }
              >
                {!roles?.roles.some(role => role.name === 'clinical_trials_manager') && (
                  <option value="clinical_trials_manager">Clinical Trials Manager</option>
                )}
                {roles?.roles
                  .filter(
                    role =>
                      !legacyMode ||
                      role.name === 'collaborator' ||
                      role.name === 'clinical_trials_manager'
                  )
                  .map(role => (
                    <option key={role.id} value={role.name}>
                      {role.display_name}
                    </option>
                  ))}
              </select>
            </div>
          ))}
          <Button
            size="sm"
            disabled={
              emails.length === 0 ||
              emails.length > (legacyMode ? 1 : 100) ||
              create.isPending ||
              legacyCreate.isPending
            }
            onClick={save}
          >
            {create.isPending || legacyCreate.isPending ? 'Saving…' : 'Preassign people'}
          </Button>
        </div>
      )}
    </section>
  );
}
